import { useState, useEffect, useCallback, useRef } from 'react';
import { QueryDocumentSnapshot, DocumentData } from 'firebase/firestore';
import { useDebouncedCallback } from 'use-debounce';
import { Customer } from '../types';
import { CustomerSortField, CustomerSortOrder } from '../components/CustomerSort';
import { 
  fetchCustomersPaginated, 
  getCustomerCount, 
  PaginatedCustomersResult,
  searchCustomers
} from '../services/customerService';

interface UsePaginatedCustomersOptions {
  pageSize?: number;
  enabled?: boolean;
}

type RushdFilterOption = 'all' | 'members' | 'non-members';
type DeviceFilterOption = 'all' | 'desktop' | 'mobile_app' | 'mobile_browser' | 'unknown';

interface SearchCustomersResult {
  customers: Customer[];
  total: number;
}

interface UsePaginatedCustomersResult {
  customers: Customer[];
  loading: boolean;
  error: string | null;
  totalCount: number;
  currentPage: number;
  totalPages: number;
  hasMore: boolean;
  searchQuery: string;
  selectedStatus: string;
  rushdFilter: RushdFilterOption;
  deviceFilter: DeviceFilterOption;
  sortField: CustomerSortField | null;
  sortOrder: CustomerSortOrder;
  isSearchMode: boolean;
  isFilterMode: boolean;
  isSortMode: boolean;
  isCombinedMode: boolean;
  searchLoading: boolean;
  filterLoading: boolean;
  sortLoading: boolean;
  setSearchQuery: (query: string) => void;
  setSelectedStatus: (status: string) => void;
  setRushdFilter: (filter: RushdFilterOption) => void;
  setDeviceFilter: (filter: DeviceFilterOption) => void;
  setSorting: (field: CustomerSortField | null, order: CustomerSortOrder) => void;
  handleSearch: (query: string) => void;
  fetchNextPage: () => Promise<void>;
  fetchPage: (page: number) => Promise<void>;
  refresh: () => Promise<void>;
  reset: () => void;
  updateCustomerInCache: (updatedCustomer: Customer) => void;
  removeCustomerFromCache: (customerId: string) => void;
  invalidateCache: () => void;
}

/**
 * Hook for paginated customer fetching with search and filtering
 * Matches the customer-facing app's search logic with debouncing, caching, and search modes
 */
export const usePaginatedCustomers = ({ 
  pageSize = 20,
  enabled = true 
}: UsePaginatedCustomersOptions = {}): UsePaginatedCustomersResult => {
  // Server customers state (for browse mode)
  const [serverCustomers, setServerCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  
  // Search state management
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [searchCache, setSearchCache] = useState<Map<string, SearchCustomersResult>>(new Map());
  const [searchResults, setSearchResults] = useState<Customer[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchCurrentPage, setSearchCurrentPage] = useState(1);
  
  // Filter state management
  const [isFilterMode, setIsFilterMode] = useState(false);
  const [filterCache, setFilterCache] = useState<Map<string, SearchCustomersResult>>(new Map());
  const [filterResults, setFilterResults] = useState<Customer[]>([]);
  const [filterLoading, setFilterLoading] = useState(false);
  const [filterCurrentPage, setFilterCurrentPage] = useState(1);
  
  // Sort state management
  const [isSortMode, setIsSortMode] = useState(false);
  const [sortCache, setSortCache] = useState<Map<string, SearchCustomersResult>>(new Map());
  const [sortResults, setSortResults] = useState<Customer[]>([]);
  const [sortLoading, setSortLoading] = useState(false);
  const [sortCurrentPage, setSortCurrentPage] = useState(1);
  
  // Combined mode state (when search, filters, and/or sorting are active)
  const [combinedResults, setCombinedResults] = useState<Customer[]>([]);
  const [combinedCurrentPage, setCombinedCurrentPage] = useState(1);
  
  // Computed state for combined mode
  const isCombinedMode = (isSearchMode && isFilterMode) || (isSearchMode && isSortMode) || (isFilterMode && isSortMode) || (isSearchMode && isFilterMode && isSortMode);
  
  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('All');
  const [rushdFilter, setRushdFilter] = useState<RushdFilterOption>('all');
  const [deviceFilter, setDeviceFilter] = useState<DeviceFilterOption>('all');
  
  // Sort state
  const [sortField, setSortField] = useState<CustomerSortField | null>(null);
  const [sortOrder, setSortOrder] = useState<CustomerSortOrder>('desc');
  
  // Final filtered customers (what gets displayed)
  const [customers, setCustomers] = useState<Customer[]>([]);
  
  // Use ref for pagination cursors to avoid dependency issues
  const pageCursorsRef = useRef<Map<number, QueryDocumentSnapshot<DocumentData> | null>>(new Map());
  
  const totalPages = Math.ceil(totalCount / pageSize);

  // Fetch all customers for search (with caching)
  const fetchCustomersForSearch = useCallback(async (): Promise<Customer[]> => {
    const cacheKey = 'search_all';
    
    // Check cache first
    const cached = searchCache.get(cacheKey);
    if (cached) {
      console.log('Using cached search data for customers');
      return cached.customers;
    }

    try {
      setSearchLoading(true);
      setError(null);
      
      const result = await searchCustomers('');
      
      // Cache the result
      setSearchCache(prev => new Map(prev).set(cacheKey, result));
      
      return result.customers;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch customers for search';
      setError(errorMessage);
      console.error('Search fetch error:', error);
      return [];
    } finally {
      setSearchLoading(false);
    }
  }, [searchCache]);

  // Fetch all customers for filtering (with caching)
  const fetchCustomersForFilter = useCallback(async (): Promise<Customer[]> => {
    const cacheKey = 'filter_all';
    
    // Check cache first
    const cached = filterCache.get(cacheKey);
    if (cached) {
      console.log('Using cached filter data for customers');
      return cached.customers;
    }

    try {
      setFilterLoading(true);
      setError(null);
      
      const result = await searchCustomers('');
      
      // Cache the result
      setFilterCache(prev => new Map(prev).set(cacheKey, result));
      
      return result.customers;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch customers for filtering';
      setError(errorMessage);
      console.error('Filter fetch error:', error);
      return [];
    } finally {
      setFilterLoading(false);
    }
  }, [filterCache]);

  // Perform client-side search
  const performSearch = useCallback((query: string, customers: Customer[]): Customer[] => {
    if (!query.trim()) return customers;
    
    const searchTerm = query.toLowerCase();
    return customers.filter(customer => {
      // Search by name and email
      if (customer.name.toLowerCase().includes(searchTerm) ||
          customer.email.toLowerCase().includes(searchTerm)) {
        return true;
      }
      
      // Search by phone number in customer.phone field
      if (customer.phone && customer.phone.includes(query)) {
        return true;
      }
      
      // Search by phone number in primary address
      if (customer.addresses && customer.addresses.length > 0) {
        const primaryAddress = customer.addresses.find(addr => addr.isDefault);
        if (primaryAddress && primaryAddress.phoneNumber && 
            primaryAddress.phoneNumber.includes(query)) {
          return true;
        }
      }
      
      return false;
    });
  }, []);

  const getDeviceCategory = useCallback((customer: Customer): DeviceFilterOption => {
    const surface = customer.lastAccessDevice?.surface;
    const origins = customer.accessDeviceOrigins || customer.lastAccessDevice?.flags || {};

    if (surface === 'desktop_browser') return 'desktop';
    if (surface === 'mobile_app_webview') return 'mobile_app';
    if (surface === 'mobile_browser') return 'mobile_browser';

    if (origins.mobileApp) return 'mobile_app';
    if (origins.desktop) return 'desktop';
    if (origins.mobileBrowser) return 'mobile_browser';

    return 'unknown';
  }, []);

  // Check if any filters are active
  const hasActiveFilters = useCallback((): boolean => {
    return selectedStatus !== 'All' || rushdFilter !== 'all' || deviceFilter !== 'all';
  }, [selectedStatus, rushdFilter, deviceFilter]);

  // Apply client-side filters to customers
  const applyClientFilters = useCallback((customers: Customer[]): Customer[] => {
    let result = [...customers];

    // Filter by status
    if (selectedStatus !== 'All') {
      result = result.filter(customer => {
        switch (selectedStatus) {
          case 'Active':
            return customer.isActive;
          case 'Inactive':
            return !customer.isActive;
          default:
            return true;
        }
      });
    }

    // Filter by Rush'd membership
    if (rushdFilter !== 'all') {
      result = result.filter(customer => {
        const isRushdMember = !!customer.rushdMembership?.isMember;
        return rushdFilter === 'members' ? isRushdMember : !isRushdMember;
      });
    }

    // Filter by device
    if (deviceFilter !== 'all') {
      result = result.filter(customer => {
        const category = getDeviceCategory(customer);
        return deviceFilter === 'unknown' ? category === 'unknown' : category === deviceFilter;
      });
    }

    return result;
  }, [selectedStatus, rushdFilter, deviceFilter, getDeviceCategory]);

  // Sort utility functions
  const getCustomerValue = useCallback((customer: Customer, field: CustomerSortField): number => {
    switch (field) {
      case 'name':
        return customer.name.toLowerCase().charCodeAt(0);
      case 'createdAt':
        if (customer.createdAt instanceof Date) {
          return customer.createdAt.getTime();
        } else if (typeof customer.createdAt === 'string') {
          return new Date(customer.createdAt).getTime();
        }
        return 0;
      case 'lastOrderDate':
        if (customer.lastOrderDate instanceof Date) {
          return customer.lastOrderDate.getTime();
        } else if (typeof customer.lastOrderDate === 'string') {
          return new Date(customer.lastOrderDate).getTime();
        }
        return 0;
      case 'totalSpent':
        return customer.totalSpent || 0;
      case 'orderCount':
        return customer.orderCount || 0;
      case 'averageOrderValue':
        return customer.averageOrderValue || 0;
      default:
        return 0;
    }
  }, []);

  // Apply sorting to customers
  const applySorting = useCallback((customers: Customer[], field: CustomerSortField, order: CustomerSortOrder): Customer[] => {
    return [...customers].sort((a, b) => {
      const aValue = getCustomerValue(a, field);
      const bValue = getCustomerValue(b, field);
      
      if (field === 'name') {
        const aName = a.name.toLowerCase();
        const bName = b.name.toLowerCase();
        return order === 'asc' ? aName.localeCompare(bName) : bName.localeCompare(aName);
      }
      
      return order === 'asc' ? aValue - bValue : bValue - aValue;
    });
  }, [getCustomerValue]);

  // Check if sorting is active
  const hasSorting = useCallback((): boolean => {
    return sortField !== null;
  }, [sortField]);

  // Fetch all customers for sorting (with caching)
  const fetchCustomersForSort = useCallback(async (): Promise<Customer[]> => {
    const cacheKey = 'sort_all';
    
    // Check cache first
    const cached = sortCache.get(cacheKey);
    if (cached) {
      console.log('Using cached sort data for customers');
      return cached.customers;
    }

    try {
      setSortLoading(true);
      setError(null);
      
      const result = await searchCustomers('');
      
      // Cache the result
      setSortCache(prev => new Map(prev).set(cacheKey, result));
      
      return result.customers;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch customers for sorting';
      setError(errorMessage);
      console.error('Sort fetch error:', error);
      return [];
    } finally {
      setSortLoading(false);
    }
  }, [sortCache]);

  // Debounced filter handler
  const debouncedFilter = useDebouncedCallback(async () => {
    if (!hasActiveFilters()) {
      // No filters active - exit filter mode
      setIsFilterMode(false);
      setFilterResults([]);
      setFilterCurrentPage(1);
      setCombinedResults([]);
      setCombinedCurrentPage(1);
      
      // If no search is active and no server customers, load the current page
      if (!isSearchMode && serverCustomers.length === 0 && !loading) {
        await loadPage(currentPage);
      }
      
      return;
    }

    // Enter filter mode
    setIsFilterMode(true);
    setFilterCurrentPage(1);
    setCombinedCurrentPage(1);
    
    if (isSearchMode) {
      // Search + Filter: Apply filters to search results
      const results = applyClientFilters(searchResults);
      setCombinedResults(results);
    } else {
      // Filter only: Fetch all customers and apply filters
      const allCustomers = await fetchCustomersForFilter();
      const results = applyClientFilters(allCustomers);
      setFilterResults(results);
    }
  }, 300);

  // Debounced search handler
  const debouncedSearch = useDebouncedCallback(async (query: string) => {
    if (!query.trim()) {
      // Empty search - exit search mode
      setIsSearchMode(false);
      setSearchResults([]);
      setSearchCurrentPage(1);
      setCombinedResults([]);
      setCombinedCurrentPage(1);
      
      // If no filters are active and no server customers, load the current page
      if (!hasActiveFilters() && serverCustomers.length === 0 && !loading) {
        await loadPage(currentPage);
      }
      
      return;
    }

    // Enter search mode and fetch all customers if needed
    setIsSearchMode(true);
    setSearchCurrentPage(1);
    setCombinedCurrentPage(1);
    
    const allCustomers = await fetchCustomersForSearch();
    const results = performSearch(query, allCustomers);
    
    setSearchResults(results);
    
    // Apply filters if active
    if (hasActiveFilters()) {
      // Search + Filter
      const combinedResults = applyClientFilters(results);
      setCombinedResults(combinedResults);
    }
  }, 300);

  // Debounced sort handler
  const debouncedSort = useDebouncedCallback(async () => {
    if (!hasSorting()) {
      // No sorting active - exit sort mode
      setIsSortMode(false);
      setSortResults([]);
      setSortCurrentPage(1);
      setCombinedResults([]);
      setCombinedCurrentPage(1);
      
      // If no search/filters active and no server customers, load the current page
      if (!isSearchMode && !isFilterMode && serverCustomers.length === 0 && !loading) {
        await loadPage(currentPage);
      }
      
      return;
    }

    // Enter sort mode
    setIsSortMode(true);
    setSortCurrentPage(1);
    setCombinedCurrentPage(1);
    
    if (isSearchMode && isFilterMode) {
      // Sort combined search + filter results
      const results = applySorting(combinedResults, sortField!, sortOrder);
      setCombinedResults(results);
    } else if (isSearchMode) {
      // Sort search results
      const results = applySorting(searchResults, sortField!, sortOrder);
      setCombinedResults(results);
    } else if (isFilterMode) {
      // Sort filter results
      const results = applySorting(filterResults, sortField!, sortOrder);
      setCombinedResults(results);
    } else {
      // Sort all customers
      const allCustomers = await fetchCustomersForSort();
      const results = applySorting(allCustomers, sortField!, sortOrder);
      setSortResults(results);
    }
  }, 300);

  // Use effect to trigger sort when sort field or order changes
  useEffect(() => {
    debouncedSort();
  }, [sortField, sortOrder, debouncedSort]);

  // Load total count
  const loadTotalCount = useCallback(async () => {
    try {
      const count = await getCustomerCount();
      setTotalCount(count);
    } catch (err) {
      console.error('Error fetching customer count:', err);
      setError('Failed to load customer count');
    }
  }, []);

  // Load customers for a specific page (browse mode only)
  const loadPage = useCallback(async (page: number) => {
    if (!enabled) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Regular pagination - no search query
      let cursor: QueryDocumentSnapshot<DocumentData> | null = null;
      
      if (page > 1) {
        cursor = pageCursorsRef.current.get(page - 1) || null;
        
        // If we don't have the cursor, we need to fetch from the beginning
        if (!cursor) {
          // Reset and fetch sequentially
          pageCursorsRef.current.clear();
          let currentCursor: QueryDocumentSnapshot<DocumentData> | null = null;
          
          for (let p = 1; p < page; p++) {
            const result = await fetchCustomersPaginated(
              pageSize,
              currentCursor
            );
            
            if (result.lastDoc) {
              pageCursorsRef.current.set(p, result.lastDoc);
              currentCursor = result.lastDoc;
            } else {
              break;
            }
          }
          
          cursor = currentCursor;
        }
      }
      
      const result = await fetchCustomersPaginated(pageSize, cursor);
      
      // Store the cursor for this page
      if (result.lastDoc) {
        pageCursorsRef.current.set(page, result.lastDoc);
      }
      
      setServerCustomers(result.customers);
      setHasMore(result.hasMore);
      setCurrentPage(page);
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load customers';
      setError(errorMessage);
      console.error('Error loading customers:', err);
    } finally {
      setLoading(false);
    }
  }, [enabled, pageSize]);

  // Use effect to select which customers to display based on current mode
  useEffect(() => {
    if (isCombinedMode) {
      // Combined mode: show combined results with pagination
      const startIndex = (combinedCurrentPage - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      setCustomers(combinedResults.slice(startIndex, endIndex));
      
      // Update total count for pagination
      setTotalCount(combinedResults.length);
    } else if (isSearchMode) {
      // Search only mode: show search results
      const startIndex = (searchCurrentPage - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      setCustomers(searchResults.slice(startIndex, endIndex));
      
      // Update total count for pagination
      setTotalCount(searchResults.length);
    } else if (isFilterMode) {
      // Filter only mode: show filter results
      const startIndex = (filterCurrentPage - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      setCustomers(filterResults.slice(startIndex, endIndex));
      
      // Update total count for pagination
      setTotalCount(filterResults.length);
    } else if (isSortMode) {
      // Sort only mode: show sort results
      const startIndex = (sortCurrentPage - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      setCustomers(sortResults.slice(startIndex, endIndex));
      
      // Update total count for pagination
      setTotalCount(sortResults.length);
    } else {
      // Browse mode: show server customers
      setCustomers(serverCustomers);
    }
  }, [
    isCombinedMode, isSearchMode, isFilterMode, isSortMode,
    combinedResults, combinedCurrentPage,
    searchResults, searchCurrentPage,
    filterResults, filterCurrentPage,
    sortResults, sortCurrentPage,
    serverCustomers, pageSize
  ]);

  // Public API functions
  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
    debouncedSearch(query);
  }, [debouncedSearch]);

  const fetchNextPage = useCallback(async () => {
    if (isCombinedMode) {
      const nextPage = combinedCurrentPage + 1;
      const maxPage = Math.ceil(combinedResults.length / pageSize);
      if (nextPage <= maxPage) {
        setCombinedCurrentPage(nextPage);
      }
    } else if (isSearchMode) {
      const nextPage = searchCurrentPage + 1;
      const maxPage = Math.ceil(searchResults.length / pageSize);
      if (nextPage <= maxPage) {
        setSearchCurrentPage(nextPage);
      }
    } else if (isFilterMode) {
      const nextPage = filterCurrentPage + 1;
      const maxPage = Math.ceil(filterResults.length / pageSize);
      if (nextPage <= maxPage) {
        setFilterCurrentPage(nextPage);
      }
    } else if (isSortMode) {
      const nextPage = sortCurrentPage + 1;
      const maxPage = Math.ceil(sortResults.length / pageSize);
      if (nextPage <= maxPage) {
        setSortCurrentPage(nextPage);
      }
    } else {
      // Browse mode pagination
      if (hasMore) {
        await loadPage(currentPage + 1);
      }
    }
  }, [
    isCombinedMode, isSearchMode, isFilterMode, isSortMode,
    combinedCurrentPage, combinedResults.length,
    searchCurrentPage, searchResults.length,
    filterCurrentPage, filterResults.length,
    sortCurrentPage, sortResults.length,
    hasMore, currentPage, loadPage, pageSize
  ]);

  const fetchPage = useCallback(async (page: number) => {
    if (isCombinedMode) {
      const maxPage = Math.ceil(combinedResults.length / pageSize);
      if (page >= 1 && page <= maxPage) {
        setCombinedCurrentPage(page);
      }
    } else if (isSearchMode) {
      const maxPage = Math.ceil(searchResults.length / pageSize);
      if (page >= 1 && page <= maxPage) {
        setSearchCurrentPage(page);
      }
    } else if (isFilterMode) {
      const maxPage = Math.ceil(filterResults.length / pageSize);
      if (page >= 1 && page <= maxPage) {
        setFilterCurrentPage(page);
      }
    } else if (isSortMode) {
      const maxPage = Math.ceil(sortResults.length / pageSize);
      if (page >= 1 && page <= maxPage) {
        setSortCurrentPage(page);
      }
    } else {
      // Browse mode pagination
      await loadPage(page);
    }
  }, [
    isCombinedMode, isSearchMode, isFilterMode, isSortMode,
    combinedResults.length, searchResults.length, filterResults.length, sortResults.length,
    loadPage, pageSize
  ]);

  const refresh = useCallback(async () => {
    // Clear all caches
    setSearchCache(new Map());
    setFilterCache(new Map());
    setSortCache(new Map());
    pageCursorsRef.current.clear();
    
    // Reset pagination states
    setSearchCurrentPage(1);
    setFilterCurrentPage(1);
    setSortCurrentPage(1);
    setCombinedCurrentPage(1);
    
    // Reload current view
    if (isCombinedMode || isSearchMode || isFilterMode || isSortMode) {
      // Re-trigger the appropriate handlers
      if (searchQuery.trim()) {
        await debouncedSearch(searchQuery);
      }
      if (hasActiveFilters()) {
        await debouncedFilter();
      }
      if (hasSorting()) {
        await debouncedSort();
      }
    } else {
      // Reload browse mode
      await Promise.all([loadPage(1), loadTotalCount()]);
    }
  }, [
    isCombinedMode, isSearchMode, isFilterMode, isSortMode,
    searchQuery, hasActiveFilters, hasSorting,
    debouncedSearch, debouncedFilter, debouncedSort, loadPage, loadTotalCount
  ]);

  const reset = useCallback(() => {
    // Clear all state
    setSearchQuery('');
    setSelectedStatus('All');
    setRushdFilter('all');
    setSortField(null);
    setSortOrder('desc');
    setIsSearchMode(false);
    setIsFilterMode(false);
    setIsSortMode(false);
    setSearchResults([]);
    setFilterResults([]);
    setSortResults([]);
    setCombinedResults([]);
    setSearchCurrentPage(1);
    setFilterCurrentPage(1);
    setSortCurrentPage(1);
    setCombinedCurrentPage(1);
    setSearchCache(new Map());
    setFilterCache(new Map());
    setSortCache(new Map());
    pageCursorsRef.current.clear();
    
    // Reload from beginning
    loadPage(1);
    loadTotalCount();
  }, [loadPage, loadTotalCount]);

  // Cache management functions
  const updateCustomerInCache = useCallback((updatedCustomer: Customer) => {
    const updateCustomerInArray = (customers: Customer[]) => 
      customers.map(customer => customer.id === updatedCustomer.id ? updatedCustomer : customer);

    const updateCacheMap = (cacheMap: Map<string, SearchCustomersResult>) => {
      const newMap = new Map(cacheMap);
      for (const [key, value] of newMap.entries()) {
        newMap.set(key, {
          ...value,
          customers: updateCustomerInArray(value.customers)
        });
      }
      return newMap;
    };

    // Update all relevant states
    setServerCustomers(prev => updateCustomerInArray(prev));
    setSearchResults(prev => updateCustomerInArray(prev));
    setFilterResults(prev => updateCustomerInArray(prev));
    setSortResults(prev => updateCustomerInArray(prev));
    setCombinedResults(prev => updateCustomerInArray(prev));
    
    // Update caches
    setSearchCache(updateCacheMap);
    setFilterCache(updateCacheMap);
    setSortCache(updateCacheMap);
  }, []);

  const removeCustomerFromCache = useCallback((customerId: string) => {
    const removeCustomerFromArray = (customers: Customer[]) => 
      customers.filter(customer => customer.id !== customerId);

    const removeFromCacheMap = (cacheMap: Map<string, SearchCustomersResult>) => {
      const newMap = new Map(cacheMap);
      for (const [key, value] of newMap.entries()) {
        newMap.set(key, {
          ...value,
          customers: removeCustomerFromArray(value.customers),
          total: removeCustomerFromArray(value.customers).length
        });
      }
      return newMap;
    };

    // Update all relevant states
    setServerCustomers(prev => removeCustomerFromArray(prev));
    setSearchResults(prev => removeCustomerFromArray(prev));
    setFilterResults(prev => removeCustomerFromArray(prev));
    setSortResults(prev => removeCustomerFromArray(prev));
    setCombinedResults(prev => removeCustomerFromArray(prev));
    
    // Update caches
    setSearchCache(removeFromCacheMap);
    setFilterCache(removeFromCacheMap);
    setSortCache(removeFromCacheMap);
    
    // Update total count
    setTotalCount(prev => Math.max(0, prev - 1));
  }, []);

  const invalidateCache = useCallback(() => {
    setSearchCache(new Map());
    setFilterCache(new Map());
    setSortCache(new Map());
    pageCursorsRef.current.clear();
  }, []);

  // Effect to trigger filtering when status changes
  useEffect(() => {
    debouncedFilter();
  }, [selectedStatus, rushdFilter, deviceFilter, debouncedFilter]);

  // Initialize data loading
  useEffect(() => {
    if (enabled) {
      const initialize = async () => {
        await Promise.all([loadPage(1), loadTotalCount()]);
      };
      
      initialize();
    }
  }, [enabled, loadPage, loadTotalCount]);

  return {
    customers,
    loading,
    error,
    totalCount,
    currentPage: isCombinedMode ? combinedCurrentPage : isSearchMode ? searchCurrentPage : isFilterMode ? filterCurrentPage : currentPage,
    totalPages,
    hasMore,
    searchQuery,
    selectedStatus,
    rushdFilter,
    deviceFilter,
    sortField,
    sortOrder,
    isSearchMode,
    isFilterMode,
    isSortMode,
    isCombinedMode,
    searchLoading,
    filterLoading,
    sortLoading,
    setSearchQuery,
    setSelectedStatus,
    setRushdFilter,
    setDeviceFilter,
    setSorting: (field: CustomerSortField | null, order: CustomerSortOrder) => {
      setSortField(field);
      setSortOrder(order);
    },
    handleSearch,
    fetchNextPage,
    fetchPage,
    refresh,
    reset,
    updateCustomerInCache,
    removeCustomerFromCache,
    invalidateCache,
  };
}; 
